// THE FLOOR HOST of spec 07 §4.4's execution screen: the ONE device outbox.
//
// Every tap goes through `useFloorSubmit` — online or not, no
// `if (online) fetch() else enqueue()` branch anywhere (floor-outbox.ts's rule
// 1). Online simply means the queue drains in ~200 ms and the screen awaits the
// real outcome; offline means the screen says "queued" and means it, because
// `enqueue` does not resolve until the record is on disk.
//
// PHOTOS ARE PERSISTED BEFORE THE FLAG (D-096), in that order and never the
// other: a process death between the two leaves an orphaned blob, which is
// inert, rather than a queued result pointing at bytes that do not exist, which
// would park on a `QUALITY_EVIDENCE_INVALID` no operator could clear. The blobs
// go into the SAME `assessly.floor` v2 photo store module 04's defect flags use
// — one store, one lifecycle, one `clear()` on device hand-over. A second store
// for quality would mean a photo uploaded twice or dropped once, which is the
// whole reason this port exists instead of a second queue.

import { useCallback, useMemo } from "react";
import { capturePhotos } from "../lib/photo-upload";
import { floorPhotoStore, useFloorOutbox, useFloorSubmit } from "./use-floor-outbox";
import type { OutboxItem, OutboxSettlement } from "../lib/floor-outbox";
import type { InspectionRunPort } from "../../office/quality/run/inspection-run-port";
import { useFailureActionArm } from "../../office/quality/run/use-failure-action-arm";

/** What `useFloorSubmit` reports when the DURABLE WRITE ITSELF failed (a full
 * device, a store the browser tore down). It is not a server refusal and must
 * not read as one — the operator has to redo the work, and the loudest possible
 * rendering is the honest one. The screen's `needs_attention` note is that
 * rendering; the failure record carries no HTTP status, which is exactly what
 * `describeFailure` produces for a transport-class problem. */
const NOT_SAVED: OutboxSettlement = {
  outcome: "needs_attention",
  failure: {
    httpStatus: undefined,
    code: "FLOOR_QUEUE_WRITE_FAILED",
    message: "",
    requestId: undefined,
    extensions: {},
    retryAfterMs: undefined,
  },
};

export function useFloorRunPort(inspectionId: string): InspectionRunPort {
  const { state, submit } = useFloorSubmit();
  const outbox = useFloorOutbox();
  // §4.4's failure-action arm (07-B-3): the ONE thing on this port that does
  // NOT ride the outbox, by decision — a disposition consumes a real subject
  // and drives three modules in one transaction, so it is taken ONLINE, and
  // offline the sheet's controls carry their reason in the slab (D-118).
  const failureActions = useFailureActionArm();

  const recordResult = useCallback<InspectionRunPort["recordResult"]>(
    async (input) => {
      let photoIds: string[] = [];
      if (input.photos.length > 0) {
        try {
          // `quality_evidence`, EXPLICITLY (D-064 pt 8): module 07's applier
          // only accepts ids stored under this purpose (§2.6), and the default
          // `production_evidence` would register fine and then park the result
          // on a purpose mismatch that cannot be fixed after queueing — the
          // 2026-08-09 defect that hit module 05's captures.
          photoIds = await capturePhotos(floorPhotoStore(), input.photos, {
            purpose: "quality_evidence",
          });
        } catch {
          return NOT_SAVED;
        }
      }
      const settlement = await submit(
        {
          kind: "quality_result",
          inspectionId: input.inspectionId,
          // The wire field is present and EMPTY at tap: the drain binds the
          // uploaded ids in at send time (`attachPhotoFileIds`), under this
          // record's own tap-time key, so a replay carries byte-identical ids.
          body: photoIds.length > 0 ? { ...input.body, photo_file_ids: [] } : input.body,
        },
        15_000,
        {
          identity: input.identity,
          ...(photoIds.length > 0 ? { photoIds } : {}),
        },
      );
      return settlement ?? NOT_SAVED;
    },
    [submit],
  );

  const complete = useCallback<InspectionRunPort["complete"]>(
    async (input) => {
      const settlement = await submit(
        { kind: "quality_complete", inspectionId: input.inspectionId, body: input.body },
        15_000,
        { identity: input.identity },
      );
      return settlement ?? NOT_SAVED;
    },
    [submit],
  );

  // WHAT THE QUEUE CURRENTLY SAYS about this inspection's held taps, keyed by
  // check item. The screen renders each card from this when the queue still
  // holds that item's tap, so a refusal that lands minutes after the tap —
  // while the operator is still on the screen — replaces the clock instead of
  // leaving it spinning forever (see `liveSettlements` on the port).
  //
  // Reads the ITEM'S OWN op body rather than any per-screen bookkeeping: the
  // queue is the truth about queued work, and a second copy of that truth would
  // be a second thing to keep in step.
  const liveSettlements = useMemo(() => {
    const byItem = new Map<string, OutboxSettlement["outcome"]>();
    const classify = (items: readonly OutboxItem[], outcome: OutboxSettlement["outcome"]): void => {
      for (const item of items) {
        if (item.op.kind !== "quality_result") continue;
        if (item.op.inspectionId !== inspectionId) continue;
        const checkItemId = item.op.body.check_item_id;
        if (typeof checkItemId === "string" && checkItemId.length > 0) {
          byItem.set(checkItemId, outcome);
        }
      }
    };
    // Order matters: a terminal state must win over "still waiting" if the same
    // item somehow appears in both — the operator needs the bad news.
    classify(outbox.pending, "queued");
    classify(outbox.needsReview, "conflicted");
    classify(outbox.needsAttention, "needs_attention");
    return byItem;
  }, [outbox.pending, outbox.needsReview, outbox.needsAttention, inspectionId]);

  return {
    recordResult,
    complete,
    busy: state.phase === "submitting",
    durableOffline: true,
    liveSettlements,
    failureActions,
  };
}
